import { SessionRoute } from "./SessionRoute";
import { getClientId } from "./UserShowOptions";

function locationKey(location: Location) {
  const clientId = getClientId(location) || "0";
  return `tab.viz.${location.pathname}#${clientId}`;
}

function sessionKey(sessionId: string) {
  return `tab.viz.session.${sessionId}`;
}

function sessionRoutesKey(sessionId: string) {
  return `tab.viz.session.routes.${sessionId}`;
}

const allManagedSessionsKey = "tab.viz.session.management.roots";

type SessionHistory = {
  lastActiveMinutesSinceEpoch: number;
  sessionIdleMinutes?: number;
};

function mapFromObject(key: string, value: unknown) {
  if (key === "" && typeof value === "object") {
    // Transform the parsed object to a map
    return new Map(Object.entries(value as Record<string, unknown>));
  }
  return value;
}

/**
 * Facade over window.localStorage for handling Location-based lookup
 * for session routing and recovery data.
 *
 * Accessed in the scriptsharp codebase as 'VizqlStorage', defined in
 * scriptsharp/src/Interop/VizqlStorage.cs
 */
export class VizqlStorage {
  private storage: Storage | null;

  constructor(storage: Storage | null) {
    this.storage = storage;
  }

  get length() {
    return this.storage?.length || 0;
  }

  private storeItem(key: string, value: string) {
    if (this.storage === null) {
      return;
    }
    try {
      this.storage.setItem(key, value);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * The code inside getAndUpdate will run, and the commit of any changes can be
   * handled by a .then().
   *
   * The browser sends StorageEvent if another window instance calls setItem
   * with a new value. If another tab changes the value under the shared key,
   * in the same storage area (localStorage, in this case), the event listener
   * rejects the promise. Programmers must handle that case (e.g. by merging
   * the newValue with the locally computed value, aborting, or retrying)
   * explicitly.
   *
   * This doesn't completely eliminate the possibility of a data race:
   * after 'getAndUpdate' computes the new string to store under the shared key,
   * an event could arrive after the promise is settled, but before the 'store'
   * function (localStorage.setItem) executes.
   *
   * Testing in Chrome, access to storage seemed to be synchronized within the
   * browser, preventing another tab from accessing localStorage after the local
   * context has called getItem.
   */
  private guardAgainstStorageUpdates(
    key: string,
    getAndUpate: (key: string) => string | null
  ): Promise<string | null> {
    return new Promise((store, interrupt) => {
      const interruptingStorageEvent = (event: StorageEvent) => {
        if (this.storage === event.storageArea && key === event.key) {
          interrupt(event.newValue);
        }
      };
      try {
        window.addEventListener("storage", interruptingStorageEvent);
        const newItem = getAndUpate(key);
        store(newItem);
      } finally {
        window.removeEventListener("storage", interruptingStorageEvent);
      }
    });
  }

  private getSessionHistory(sessionId: string): SessionHistory | null {
    if (this.storage === null) {
      return null;
    }
    const sessionRecord = this.storage.getItem(sessionKey(sessionId));
    return sessionRecord ? (JSON.parse(sessionRecord) as SessionHistory) : null;
  }

  private isExpired(sessionId: string): boolean {
    const sessionRecord = this.getSessionHistory(sessionId);
    if (sessionRecord) {
      // Configurable at the session-level:
      // see scriptsharp/src/Domain/Session/BaseSession.cs
      // UpdateLastSuccessfulServerCommand
      const ignoreRecordsOlderThan =
        Date.now() -
        1000 * // milliseconds -> seconds
          60 * // seconds -> minutes
          (sessionRecord.sessionIdleMinutes || 60);
      return (
        ignoreRecordsOlderThan >
        1000 * 60 * sessionRecord.lastActiveMinutesSinceEpoch
      );
    }
    return false;
  }

  public async storeSessionRoute(location: Location, route: SessionRoute) {
    if (this.storage === null) {
      return;
    }
    const key = locationKey(location);
    const newRoute = JSON.stringify(route);

    // No need to update storage if the current most recently stored
    // routing keys match the requested ones.
    const previousRoute = this.storage.getItem(key);
    if (previousRoute === newRoute) {
      return;
    }

    this.storeItem(key, newRoute);

    const routesKey = sessionRoutesKey(route.vizqlSessionRoute);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const routes = new Set(JSON.parse(this.storage.getItem(routesKey) || "[]"));
    routes.add(key);
    this.storeItem(routesKey, JSON.stringify(Array.of(...routes)));

    let updated = false;
    let retries = 0;
    while (!updated && retries < 10) {
      await this.guardAgainstStorageUpdates(
        allManagedSessionsKey,
        (sharedKey) => {
          if (this.storage === null) {
            return null;
          }
          const allSessions = this.storage.getItem(sharedKey);
          const sessions = JSON.parse(
            allSessions || "{}",
            mapFromObject
          ) as Map<string, number>;

          sessions.set(route.vizqlSessionRoute, Date.now());
          return JSON.stringify(Object.fromEntries(sessions));
        }
      )
        .then((updatedValue: string | null) => {
          if (updatedValue !== null) {
            this.storeItem(allManagedSessionsKey, updatedValue);
            updated = true;
          }
        })
        .catch(() => {
          retries += 1;
        });
    }
  }

  public getSessionRoute(location: Location): SessionRoute | null {
    if (this.storage === null) {
      return null;
    }
    const json = this.storage.getItem(locationKey(location));
    if (json) {
      const route = JSON.parse(json) as SessionRoute;
      return this.isExpired(route.vizqlSessionRoute) ? null : route;
    }
    return null;
  }

  public updateSession(
    sessionId: string,
    now: number,
    sessionData: Record<string, unknown>
  ) {
    const key = sessionKey(sessionId);
    this.storeItem(
      key,
      JSON.stringify({
        lastActiveMinutesSinceEpoch: Math.floor(now / (60 * 1000)),
        ...sessionData,
      })
    );
  }

  public getSessionJson(location: Location): string | null {
    if (this.storage === null) {
      return null;
    }

    const route = this.getSessionRoute(location);
    if (route) {
      const key = sessionKey(route.vizqlSessionRoute);
      return this.storage.getItem(key);
    }
    return null;
  }

  public async purgeExpiredSessions() {
    return this.purge(
      Date.now() -
        1_000 * // milliseconds -> seconds
          60 * // seconds -> minutes
          60 * // minutes -> hours
          24 * // hours -> days
          4 // 4 days ago
    );
  }

  public async purge(since: number) {
    if (this.storage === null) {
      return;
    }
    await this.guardAgainstStorageUpdates(
      allManagedSessionsKey,
      (sharedKey) => {
        if (this.storage === null) {
          return null;
        }
        const storage = this.storage;
        const activeSessions = new Map<string, number>();
        const item = storage.getItem(sharedKey);
        const sessionsWithExpiration = JSON.parse(
          item || "{}",
          mapFromObject
        ) as Map<string, number>;
        if (sessionsWithExpiration) {
          sessionsWithExpiration.forEach((createTime, sessionId, _) => {
            if (createTime > since) {
              activeSessions.set(sessionId, createTime);
            } else {
              storage.removeItem(sessionKey(sessionId));

              const routesKey = sessionRoutesKey(sessionId);
              const routes = JSON.parse(storage.getItem(routesKey) || "[]") as [
                string
              ];
              if (routes) {
                storage.removeItem(routesKey);
                routes.forEach((loc) => {
                  storage.removeItem(loc);
                });
              }
            }
          });
        }
        return JSON.stringify(Object.fromEntries(activeSessions));
      }
    )
      .then((activeSessions: string | null) => {
        if (activeSessions !== null) {
          this.storeItem(allManagedSessionsKey, activeSessions);
        }
      })
      .catch(() => console.log("Deferring session management deletions"));
  }

  public removeLocation(location: Location) {
    if (this.storage === null) {
      return;
    }
    const key = locationKey(location);
    this.storage.removeItem(key);
  }
}
